import { useEffect, useState } from 'react';
import {
    api,
    type ActivityGrowthSummary,
    type GrowthMemoryCard,
    type GrowthRing,
    type MusicVibeSharing,
    type PalProfileResponse,
    type StampCollectionProgress,
    type ToneVariant,
} from '../cloud/api';
import { FaceState } from '../pal/face';
import { TONE_VARIANTS } from '../vibe/onboarding';
import { palProfileToFaceProps } from './FacePropAdapter';
import { MusicVibeConsentDialog } from './MusicVibeConsentDialog';
import { PalFaceCartoon } from './PalFaceCartoon';

export function VibeProfileScreen({ pal, onPalUpdated }: {
    pal: PalProfileResponse;
    onPalUpdated: (p: PalProfileResponse) => void;
}) {
    const [growth, setGrowth] = useState<ActivityGrowthSummary | null>(null);
    const [growthLoading, setGrowthLoading] = useState(true);
    const [growthError, setGrowthError] = useState<string | null>(null);
    const [confirmForgetId, setConfirmForgetId] = useState<string | null>(null);
    const [forgettingId, setForgettingId] = useState<string | null>(null);
    const [forgetError, setForgetError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setGrowthLoading(true);
        setGrowthError(null);

        void api.activityGrowth()
            .then((next) => {
                if (!cancelled) setGrowth(next);
            })
            .catch((err) => {
                if (!cancelled) setGrowthError((err as Error).message);
            })
            .finally(() => {
                if (!cancelled) setGrowthLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [pal.pal_id]);

    async function refreshGrowth() {
        const next = await api.activityGrowth();
        setGrowth(next);
    }

    async function forgetMemory(memoryId: string) {
        if (forgettingId) return;
        setForgettingId(memoryId);
        setForgetError(null);
        try {
            await api.activityMemoryDelete(memoryId);
            setConfirmForgetId(null);
            await refreshGrowth();
        } catch (err) {
            setForgetError((err as Error).message);
        } finally {
            setForgettingId(null);
        }
    }

    return (
        <div className="space-y-4">
            <PalIdentityCard pal={pal} />
            <GrowthRingsCard growth={growth} loading={growthLoading} error={growthError} />
            {growth && (
                <>
                    <FocusedMemoriesCard
                        memories={growth.memories}
                        confirmForgetId={confirmForgetId}
                        forgettingId={forgettingId}
                        error={forgetError}
                        onAskForget={setConfirmForgetId}
                        onCancelForget={() => setConfirmForgetId(null)}
                        onForget={forgetMemory}
                    />
                    <GrowthCollectionsCard collections={growth.collections} />
                </>
            )}
            <VibeClassesCard pal={pal} />
            <SignalTogglesCard pal={pal} onPalUpdated={onPalUpdated} />
            <ToneCard pal={pal} onPalUpdated={onPalUpdated} />
        </div>
    );
}

function PalIdentityCard({ pal }: { pal: PalProfileResponse }) {
    const face = palProfileToFaceProps(pal);
    return (
        <div className="card">
            <div className="grid grid-cols-[minmax(0,1fr)_150px] gap-4 items-center">
                <div>
                    <p className="muted text-xs uppercase tracking-wide">Your Pal</p>
                    <h2 className="text-2xl font-semibold">{pal.pal_name}</h2>
                    <p className="muted text-sm mt-1">
                        {pretty(pal.profile.active_form)} - {pal.pal_age_days}d old
                    </p>
                    <div className="flex gap-2 mt-3 text-xs">
                        <Badge label={`${pal.glow} glow`} />
                        <Badge label={`${pal.marks_count} marks`} />
                        <Badge label={pretty(pal.profile.privacy_mode)} />
                    </div>
                </div>
                <PalFaceCartoon
                    state={FaceState.IDLE}
                    {...face}
                    marks={pal.marks_count}
                    maxWidth={150}
                    active
                />
            </div>
        </div>
    );
}

function GrowthRingsCard({ growth, loading, error }: {
    growth: ActivityGrowthSummary | null;
    loading: boolean;
    error: string | null;
}) {
    return (
        <div className="card">
            <div className="flex justify-between items-baseline mb-3">
                <h3 className="font-semibold">Growth</h3>
                {growth && <span className="muted text-xs">Updated {formatDate(growth.generated_at)}</span>}
            </div>
            {loading && <p className="muted text-sm">Loading growth...</p>}
            {!loading && error && (
                <p className="text-danger text-sm">Growth is unavailable right now.</p>
            )}
            {!loading && !error && growth && (
                <div className="grid grid-cols-2 gap-2">
                    {growth.rings.map((ring) => (
                        <GrowthRingTile key={ring.kind} ring={ring} />
                    ))}
                </div>
            )}
        </div>
    );
}

function GrowthRingTile({ ring }: { ring: GrowthRing }) {
    return (
        <div className="rounded-lg border border-line p-3 bg-surface-raised/40">
            <div className="flex justify-between items-start gap-2">
                <div>
                    <div className="font-medium text-sm">{ring.label}</div>
                    <div className="muted text-xs">{ringKindLabel(ring.kind)}</div>
                </div>
                <span className="rounded-full bg-brand/15 px-2 py-0.5 text-xs font-medium">L{ring.level}</span>
            </div>
            <p className="muted text-xs mt-2">{ringStatLine(ring)}</p>
        </div>
    );
}

function FocusedMemoriesCard({ memories, confirmForgetId, forgettingId, error, onAskForget, onCancelForget, onForget }: {
    memories: GrowthMemoryCard[];
    confirmForgetId: string | null;
    forgettingId: string | null;
    error: string | null;
    onAskForget: (id: string) => void;
    onCancelForget: () => void;
    onForget: (id: string) => void;
}) {
    return (
        <div className="card">
            <h3 className="font-semibold mb-2">Focused memories</h3>
            {memories.length === 0 ? (
                <p className="muted text-sm">Save a drive or stamp a place to start giving your Pal focused memory.</p>
            ) : (
                <div className="space-y-2">
                    {memories.map((memory) => (
                        <FocusedMemoryRow
                            key={memory.id}
                            memory={memory}
                            confirming={confirmForgetId === memory.id}
                            busy={forgettingId === memory.id}
                            onAskForget={() => onAskForget(memory.id)}
                            onCancelForget={onCancelForget}
                            onForget={() => onForget(memory.id)}
                        />
                    ))}
                </div>
            )}
            {error && <p className="text-danger text-xs mt-2">{error}</p>}
        </div>
    );
}

function FocusedMemoryRow({ memory, confirming, busy, onAskForget, onCancelForget, onForget }: {
    memory: GrowthMemoryCard;
    confirming: boolean;
    busy: boolean;
    onAskForget: () => void;
    onCancelForget: () => void;
    onForget: () => void;
}) {
    return (
        <div className="rounded-lg border border-line p-3">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="text-sm font-medium">{memory.summary}</p>
                    <p className="muted text-xs mt-1">
                        {pretty(memory.memory_type)} - updated {formatDate(memory.updated_at)}
                    </p>
                </div>
                {!confirming && (
                    <button
                        type="button"
                        className="muted text-xs underline shrink-0"
                        onClick={onAskForget}
                    >
                        Forget
                    </button>
                )}
            </div>
            {confirming && (
                <div className="flex items-center gap-2 mt-3">
                    <span className="muted text-xs">Forget this focused memory?</span>
                    <button
                        type="button"
                        className="btn-secondary text-xs py-1 px-2"
                        onClick={onCancelForget}
                        disabled={busy}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn-primary text-xs py-1 px-2"
                        onClick={onForget}
                        disabled={busy}
                    >
                        {busy ? 'Forgetting...' : 'Yes, forget'}
                    </button>
                </div>
            )}
        </div>
    );
}

function GrowthCollectionsCard({ collections }: { collections: StampCollectionProgress[] }) {
    return (
        <div className="card">
            <h3 className="font-semibold mb-2">Collections</h3>
            {collections.length === 0 ? (
                <p className="muted text-sm">No collection progress yet.</p>
            ) : (
                <div className="space-y-2">
                    {collections.map((collection) => (
                        <div key={collection.id} className="rounded-lg border border-line p-3">
                            <div className="flex justify-between gap-3 text-sm">
                                <span className="font-medium">{collection.title}</span>
                                <span className="muted">{collection.progress}/{collection.target}</span>
                            </div>
                            <div className="h-2 rounded-full bg-line mt-2 overflow-hidden">
                                <div
                                    className="h-full bg-brand"
                                    style={{ width: `${progressPct(collection.progress, collection.target)}%` }}
                                />
                            </div>
                            <p className="muted text-xs mt-2">{collection.description}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function VibeClassesCard({ pal }: { pal: PalProfileResponse }) {
    return (
        <div className="card">
            <h3 className="font-semibold mb-2">Vibe classes</h3>
            <p className="muted text-xs mb-2">
                Nightly digestion nudges these based on your own saved activity.
            </p>
            {pal.profile.vibe_classes.map((vc) => (
                <div key={vc.name} className="flex justify-between mb-1">
                    <span>{pretty(vc.name)}</span>
                    <span className="font-mono text-sm">{vc.score.toFixed(2)}</span>
                </div>
            ))}
        </div>
    );
}

function ToneCard({ pal, onPalUpdated }: {
    pal: PalProfileResponse;
    onPalUpdated: (p: PalProfileResponse) => void;
}) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function update(tone: ToneVariant) {
        if (busy) return;
        setBusy(true);
        setError(null);
        try {
            const fresh = await api.palPatch({ tone_preference: tone });
            onPalUpdated(fresh);
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="card">
            <h3 className="font-semibold mb-2">Moji tone</h3>
            <div className="space-y-1">
                {TONE_VARIANTS.map((t) => {
                    const selected = pal.profile.tone_preference === t.value;
                    return (
                        <button
                            key={t.value}
                            type="button"
                            disabled={busy || selected}
                            onClick={() => update(t.value)}
                            className={`w-full p-2 rounded-lg text-left border text-sm transition ${
                                selected ? 'bg-brand/15 border-brand' : 'border-line hover:border-brand/50'
                            }`}
                        >
                            <span className="font-medium">{t.label}</span>
                            <span className="muted text-xs ml-2">{t.blurb}</span>
                        </button>
                    );
                })}
            </div>
            {error && <p className="text-danger text-xs mt-2">{error}</p>}
        </div>
    );
}

function SignalTogglesCard({ pal, onPalUpdated }: {
    pal: PalProfileResponse;
    onPalUpdated: (p: PalProfileResponse) => void;
}) {
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [consentTarget, setConsentTarget] = useState<Exclude<MusicVibeSharing, 'off'> | null>(null);
    const [error, setError] = useState<string | null>(null);

    async function toggle(key: BooleanSignalToggle) {
        if (busyKey) return;
        setBusyKey(key);
        setError(null);
        try {
            const next = !pal.profile.signal_toggles[key];
            const fresh = await api.palPatch({
                signal_toggles: { [key]: next } as Partial<PalProfileResponse['profile']['signal_toggles']>,
            });
            onPalUpdated(fresh);
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setBusyKey(null);
        }
    }

    async function updateMusicVibe(chosen: MusicVibeSharing) {
        if (busyKey) return;
        setBusyKey('music_vibe_sharing');
        setError(null);
        try {
            const fresh = await api.palPatch({ signal_toggles: { music_vibe_sharing: chosen } });
            onPalUpdated(fresh);
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setBusyKey(null);
        }
    }

    async function handleConsentClose(chosen: MusicVibeSharing | null) {
        setConsentTarget(null);
        if (chosen) await updateMusicVibe(chosen);
    }

    const entries: Array<{ key: BooleanSignalToggle; label: string; blurb: string }> = [
        {
            key: 'experience_sharing',
            label: 'Experience sharing',
            blurb: 'Share anonymized place visits with the network.',
        },
        {
            key: 'presence_sharing',
            label: 'Presence',
            blurb: 'Show your geographic-cell presence in aggregations.',
        },
        {
            key: 'alert_participation',
            label: 'Alerts',
            blurb: 'Send and receive speed-trap and hazard alerts.',
        },
        {
            key: 'trend_signals',
            label: 'Trend signals',
            blurb: 'Visit-velocity feeds the trending view.',
        },
    ];

    return (
        <div className="card">
            <h3 className="font-semibold mb-2">What you share</h3>
            <p className="muted text-xs mb-3">
                Each toggle is independent. Music sharing sends only genre and mood buckets. Specific
                songs, meals, and conversations stay private.
            </p>
            {entries.map((e) => (
                <button
                    key={e.key}
                    type="button"
                    onClick={() => toggle(e.key)}
                    disabled={busyKey === e.key}
                    className="w-full flex items-center justify-between gap-3 p-2 hover:bg-surface-raised rounded-lg text-left"
                >
                    <div>
                        <div className="font-medium text-sm">{e.label}</div>
                        <div className="muted text-xs">{e.blurb}</div>
                    </div>
                    <ToggleSwitch on={pal.profile.signal_toggles[e.key]} />
                </button>
            ))}
            <div className="mt-3 pt-3 border-t border-line">
                <div className="font-medium text-sm mb-1">Music vibe map</div>
                <div className="muted text-xs mb-2">
                    Share only genre and mood buckets. Specific songs and listening apps stay on your phone.
                </div>
                <div className="grid gap-2">
                    <MusicVibeOption
                        label="Off"
                        blurb="No music vibe signal leaves this device."
                        selected={pal.profile.signal_toggles.music_vibe_sharing === 'off'}
                        disabled={busyKey === 'music_vibe_sharing'}
                        onClick={() => void updateMusicVibe('off')}
                    />
                    <MusicVibeOption
                        label="At places"
                        blurb="Attach genre and mood to venue Echoes after privacy thresholds."
                        selected={pal.profile.signal_toggles.music_vibe_sharing === 'places_only'}
                        disabled={busyKey === 'music_vibe_sharing'}
                        onClick={() => setConsentTarget('places_only')}
                    />
                    <MusicVibeOption
                        label="Places + routes"
                        blurb="Also allow coarse route-cell music vibe aggregation while driving."
                        selected={pal.profile.signal_toggles.music_vibe_sharing === 'places_and_routes'}
                        disabled={busyKey === 'music_vibe_sharing'}
                        onClick={() => setConsentTarget('places_and_routes')}
                    />
                </div>
            </div>
            <p className="muted text-xs mt-3">
                Voice setup lives in Settings; Profile never starts the driving voice flow.
            </p>
            {error && <p className="text-danger text-xs mt-2">{error}</p>}
            {consentTarget && (
                <MusicVibeConsentDialog target={consentTarget} onClose={handleConsentClose} />
            )}
        </div>
    );
}

type BooleanSignalToggle = Exclude<keyof PalProfileResponse['profile']['signal_toggles'], 'music_vibe_sharing'>;

function MusicVibeOption({ label, blurb, selected, disabled, onClick }: {
    label: string;
    blurb: string;
    selected: boolean;
    disabled: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            disabled={disabled || selected}
            onClick={onClick}
            className={`w-full text-left p-2 rounded-lg border text-sm ${
                selected ? 'bg-brand/10 border-brand' : 'border-line hover:border-brand/50'
            }`}
        >
            <div className="font-medium">{label}</div>
            <div className="muted text-xs">{blurb}</div>
        </button>
    );
}

function ToggleSwitch({ on }: { on: boolean }) {
    return (
        <span className={`inline-block w-9 h-5 rounded-full transition ${on ? 'bg-brand' : 'bg-line'}`}>
            <span className={`inline-block w-4 h-4 mt-0.5 rounded-full bg-surface transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </span>
    );
}

function Badge({ label }: { label: string }) {
    return (
        <span className="rounded-full bg-surface-raised px-2 py-1 text-xs font-medium">
            {label}
        </span>
    );
}

function ringKindLabel(kind: GrowthRing['kind']): string {
    switch (kind) {
        case 'driving':
            return 'Drives';
        case 'places':
            return 'Places';
        case 'social':
            return 'Echoes';
        case 'music':
            return 'Music';
    }
}

function ringStatLine(ring: GrowthRing): string {
    switch (ring.kind) {
        case 'driving':
            return [
                `${ring.stats.saved_drives} saved drives`,
                `${ring.stats.clean_drive_streak} clean streak`,
                ring.stats.latest_smoothness_pct === undefined ? null : `${ring.stats.latest_smoothness_pct}% smooth`,
            ].filter(Boolean).join(' - ');
        case 'places':
            return `${ring.stats.save_count} saved, ${ring.stats.visit_count} visits, ${ring.stats.drive_count} drives`;
        case 'social':
            return `${ring.stats.echo_count} Echoes, ${ring.stats.reaction_count} reactions`;
        case 'music':
            return ring.stats.music_vibe_mode === 'off'
                ? 'Music vibe is off'
                : `${pretty(ring.stats.music_vibe_mode)} - ${ring.stats.music_tagged_echo_count} tagged Echoes`;
    }
}

function progressPct(progress: number, target: number): number {
    if (target <= 0) return 0;
    return Math.max(0, Math.min(100, Math.round((progress / target) * 100)));
}

function formatDate(unixSeconds: number): string {
    if (!Number.isFinite(unixSeconds) || unixSeconds <= 0) return 'recently';
    return new Date(unixSeconds * 1000).toLocaleDateString();
}

function pretty(s: string): string {
    return s.split('_').map((w) => w[0]!.toUpperCase() + w.slice(1)).join(' ');
}
